import React, { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../data/mock';
import { useTheme } from '../contexts/ThemeContext';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'About', to: 'about' },
    { label: 'Achievements', to: 'achievements' },
    { label: 'Case Studies', to: 'caseStudies' },
    { label: 'Services', to: 'services' },
    { label: 'Testimonials', to: 'testimonials' }
  ];

  // Contact item separate to keep at end
  const contactItem = { label: 'Contact', to: 'contact' };

  const accentColor = theme.colors.accent;
  const bgDark = theme.colors.backgroundDark;

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 text-white transition-all duration-300 ${
        scrolled ? 'bg-opacity-95 shadow-lg backdrop-blur-sm' : 'bg-opacity-0'
      }`}
      style={{
        backgroundColor: scrolled ? `${bgDark}f2` : `${bgDark}b3`
      }}
    >
      <nav className="container mx-auto flex items-center justify-between px-6 py-4">
        <RouterLink to="/" className="flex items-center gap-3 cursor-pointer text-white hover:opacity-90">
          <img
            src={personalInfo.logoMark}
            alt="VS Logo"
            className="h-11 w-11 flex-shrink-0 rounded-full object-contain md:h-12 md:w-12"
            data-testid="header-logo"
          />
          <div>
            <span className="font-serif text-xl font-bold tracking-tight text-white md:text-2xl">Venkat Swaroop</span>
            <p className="text-xs text-white/80 md:text-sm">Global Talent Acquisition Leader</p>
          </div>
        </RouterLink>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-8 lg:flex">
          {navItems.map((item) => (
            isHomePage ? (
              <ScrollLink
                key={item.to}
                to={item.to}
                spy={true}
                smooth={true}
                offset={-80}
                duration={500}
                className="cursor-pointer text-sm font-medium text-white transition-all"
                activeClass="underline"
                style={{ '--hover-color': accentColor }}
                onMouseEnter={(e) => e.target.style.color = accentColor}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                {item.label}
              </ScrollLink>
            ) : (
              <button
                key={item.to}
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const element = document.getElementById(item.to);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="cursor-pointer text-sm font-medium text-white transition-all"
                onMouseEnter={(e) => e.target.style.color = accentColor}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                {item.label}
              </button>
            )
          ))}
          <RouterLink
            to="/blog"
            className="cursor-pointer text-sm font-medium text-white transition-all"
            onMouseEnter={(e) => e.target.style.color = accentColor}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            Blog
          </RouterLink>
          <RouterLink
            to="/sourcing-hub"
            className="cursor-pointer text-sm font-medium text-white transition-all"
            onMouseEnter={(e) => e.target.style.color = accentColor}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            Sourcing Hub
          </RouterLink>
          <RouterLink
            to="/gallery"
            className="cursor-pointer text-sm font-medium text-white transition-all"
            onMouseEnter={(e) => e.target.style.color = accentColor}
            onMouseLeave={(e) => e.target.style.color = 'white'}
          >
            Gallery
          </RouterLink>
          {isHomePage ? (
            <ScrollLink
              to={contactItem.to}
              spy={true}
              smooth={true}
              offset={-80}
              duration={500}
              className="cursor-pointer text-sm font-medium text-white transition-all"
              activeClass="underline"
              onMouseEnter={(e) => e.target.style.color = accentColor}
              onMouseLeave={(e) => e.target.style.color = 'white'}
            >
              {contactItem.label}
            </ScrollLink>
          ) : (
            <button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const element = document.getElementById(contactItem.to);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="cursor-pointer text-sm font-medium text-white transition-all"
              onMouseEnter={(e) => e.target.style.color = accentColor}
              onMouseLeave={(e) => e.target.style.color = 'white'}
            >
              {contactItem.label}
            </button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 lg:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white"
            style={{ '--hover-color': accentColor }}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="absolute left-0 right-0 top-full border-t lg:hidden"
          style={{
            backgroundColor: bgDark,
            borderColor: `${bgDark}80`
          }}
        >
          <div className="flex flex-col px-6 py-4">
            {navItems.map((item) => (
              isHomePage ? (
                <ScrollLink
                  key={item.to}
                  to={item.to}
                  spy={true}
                  smooth={true}
                  offset={-80}
                  duration={500}
                  className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ '--hover-color': accentColor }}
                >
                  {item.label}
                </ScrollLink>
              ) : (
                <button
                  key={item.to}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/');
                    setTimeout(() => {
                      const element = document.getElementById(item.to);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all text-left"
                >
                  {item.label}
                </button>
              )
            ))}
            <RouterLink
              to="/blog"
              className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              Blog
            </RouterLink>
            <RouterLink
              to="/sourcing-hub"
              className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              Sourcing Hub
            </RouterLink>
            <RouterLink
              to="/gallery"
              className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              Gallery
            </RouterLink>
            {isHomePage ? (
              <ScrollLink
                to={contactItem.to}
                spy={true}
                smooth={true}
                offset={-80}
                duration={500}
                className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                {contactItem.label}
              </ScrollLink>
            ) : (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/');
                  setTimeout(() => {
                    const element = document.getElementById(contactItem.to);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="cursor-pointer border-b border-white/10 py-3 text-sm font-medium text-white transition-all text-left"
              >
                {contactItem.label}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
